import jsPDF from "jspdf";
import { toast } from "react-toastify";
import numenteLogo from "../assets/Numente.png";
import { BrushScriptMTFontBase64 } from "../assets/fonts/BrushScriptMTFont";

const FollowUpPDFGenerator = ({
  sessionFormData,
  clientName,
  providerName,
  providerCredentials,
  appointmentDate,
  appointmentTime,
  clientDOB,
  isSigned,
  getSignedDateFromDB,
  getSignedTimeFromDB,
  formatDate,
  derivedDurationMinutes,
  addendumDataArray,
}) => {
  const generatePDF = async () => {
    try {
      // Generate PDF with custom layout
      const doc = new jsPDF();
      
      // Load Brush Script MT font for signature
      doc.addFileToVFS("BrushScriptMT.ttf", BrushScriptMTFontBase64);
      doc.addFont("BrushScriptMT.ttf", "BrushScriptMT", "normal");
      
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Helper function to check if new page is needed
      const checkNewPage = (requiredHeight = 20) => {
        if (yPosition + requiredHeight > pageHeight - 30) {
          doc.addPage();
          addHeader(); // Add header to new page
          yPosition = headerHeight + 10; // Start below header
          return true;
        }
        return false;
      };

      // Helper function to add text with wrapping
      const addText = (text, x, y, maxWidth, fontSize = 10, isBold = false, color = [0, 0, 0]) => {
        doc.setFontSize(fontSize);
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.setTextColor(color[0], color[1], color[2]);
        const lines = doc.splitTextToSize(text || "N/A", maxWidth);
        doc.text(lines, x, y);
        return lines.length * (fontSize * 0.4);
      };

      // Helper function to add section header
      const addSectionHeader = (title) => {
        checkNewPage(20);
        yPosition += 10;
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(33, 37, 41);
        doc.text(title, margin, yPosition);
        yPosition += 8;
      };

      // Helper function to add field (label above value)
      const addField = (label, value, isInline = false) => {
        checkNewPage(15);
        if (isInline) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(`${label}:`, margin, yPosition);
          doc.setFont("helvetica", "normal");
          const labelWidth = doc.getTextWidth(`${label}:`);
          doc.text(value || "N/A", margin + labelWidth + 5, yPosition);
          yPosition += 6;
        } else {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(label, margin, yPosition);
          yPosition += 5;
          doc.setFont("helvetica", "normal");
          const textHeight = addText(value || "N/A", margin, yPosition, contentWidth, 10, false);
          yPosition += textHeight + 8;
        }
      };

      // Helper function to add bullet list
      const addBulletList = (items) => {
        if (!items || !Array.isArray(items) || items.length === 0) return;
        items.forEach((item) => {
          checkNewPage(8);
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text("•", margin, yPosition);
          const textHeight = addText(String(item), margin + 6, yPosition, contentWidth - 6, 10, false);
          yPosition += Math.max(textHeight, 6) + 2;
        });
        yPosition += 4;
      };

      // Load logo image once and store as base64
      let logoBase64 = null;
      let logoWidth = 40;
      let logoHeight = 0;
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((resolve) => {
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              logoBase64 = canvas.toDataURL('image/png');
              logoHeight = (img.height / img.width) * logoWidth;
              resolve();
            } catch (error) {
              console.error("Error processing logo:", error);
              resolve();
            }
          };
          img.onerror = () => {
            console.error("Error loading logo image");
            resolve();
          };
          img.src = numenteLogo;
        });
      } catch (error) {
        console.error("Error loading logo:", error);
      }

      // Helper function to add header on each page
        const addHeader = () => {
            const headerY = 0;
            const HEADER_HEIGHT = 25;
        
            // Header background
            doc.setFillColor(245, 245, 245);
            doc.rect(0, headerY, pageWidth, HEADER_HEIGHT, "F");
        
            // Logo
            if (logoBase64) {
            const logoY = headerY + (HEADER_HEIGHT - logoHeight) / 2;
            doc.addImage(logoBase64, "PNG", margin, logoY, logoWidth, logoHeight);
            }
        
            // Right-side client text
            const clientNameText = clientName || "N/A";
            const dobFormatted = clientDOB
            ? new Date(clientDOB).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
                })
            : "N/A";
        
            const clientDOBText = `DOB: ${dobFormatted}`;
        
            // Measure widths using real fonts
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            const clientNameWidth = doc.getTextWidth(clientNameText);
        
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            const clientDOBWidth = doc.getTextWidth(clientDOBText);
        
            const maxTextWidth = Math.max(clientNameWidth, clientDOBWidth);
        
            // === TEXT COLOR BLACK ===
            doc.setTextColor(0, 0, 0);
        
            // Draw name (bold)
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text(
            clientNameText,
            pageWidth - margin - maxTextWidth,
            headerY + HEADER_HEIGHT / 2 - 1
            );

            // Draw DOB (normal)
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(
            clientDOBText,
            pageWidth - margin - maxTextWidth,
            headerY + HEADER_HEIGHT / 2 + 3
            );
        };
  

      // Add header to first page
      addHeader();

      // Set yPosition to start content below header
      const headerHeight = 25;
      yPosition = headerHeight + 10;

      // Add "Appointment Details" title
      addSectionHeader("Appointment Details");

      // Appointment Details - Two column layout with inline labels and values
      let currentY = yPosition;
      const halfWidth = contentWidth / 2;
      
      // Row 1: Client and Provider (inline)
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const clientLabel = "Client:";
      doc.text(clientLabel, margin, currentY);
      doc.setFont("helvetica", "normal");
      const clientLabelWidth = doc.getTextWidth(clientLabel);
      const clientNameValue = clientName || "N/A";
      doc.text(clientNameValue, margin + clientLabelWidth + 5, currentY);
      
      doc.setFont("helvetica", "bold");
      const providerLabel = "Provider:";
      doc.text(providerLabel, margin + halfWidth, currentY);
      doc.setFont("helvetica", "normal");
      const providerLabelWidth = doc.getTextWidth(providerLabel);
      doc.text(providerName, margin + halfWidth + providerLabelWidth + 5, currentY);
      currentY += 10;

      // Row 2: Date and Scheduled Time (inline)
      doc.setFont("helvetica", "bold");
      const dateLabel = "Date:";
      doc.text(dateLabel, margin, currentY);
      doc.setFont("helvetica", "normal");
      const dateLabelWidth = doc.getTextWidth(dateLabel);
      doc.text(formatDate(appointmentDate) || "N/A", margin + dateLabelWidth + 5, currentY);
      
      doc.setFont("helvetica", "bold");
      const scheduledTimeLabel = "Scheduled Time:";
      doc.text(scheduledTimeLabel, margin + halfWidth, currentY);
      doc.setFont("helvetica", "normal");
      const scheduledTimeLabelWidth = doc.getTextWidth(scheduledTimeLabel);
      doc.text(appointmentTime || "N/A", margin + halfWidth + scheduledTimeLabelWidth + 5, currentY);
      currentY += 10;

      // Row 3: Time In and Time Out (inline)
      doc.setFont("helvetica", "bold");
      const timeInLabel = "Time In:";
      doc.text(timeInLabel, margin, currentY);
      doc.setFont("helvetica", "normal");
      const timeInLabelWidth = doc.getTextWidth(timeInLabel);
      doc.text(sessionFormData.timeIn || "N/A", margin + timeInLabelWidth + 5, currentY);
      
      doc.setFont("helvetica", "bold");
      const timeOutLabel = "Time Out:";
      doc.text(timeOutLabel, margin + halfWidth, currentY);
      doc.setFont("helvetica", "normal");
      const timeOutLabelWidth = doc.getTextWidth(timeOutLabel);
      doc.text(sessionFormData.timeOut || "N/A", margin + halfWidth + timeOutLabelWidth + 5, currentY);
      currentY += 10;

      // Row 4: Duration (inline)
      doc.setFont("helvetica", "bold");
      const durationLabel = "Session Length:";
      doc.text(durationLabel, margin, currentY);
      doc.setFont("helvetica", "normal");
      const durationLabelWidth = doc.getTextWidth(durationLabel);
      const durationText = derivedDurationMinutes ? `${derivedDurationMinutes}m` : "N/A";
      doc.text(durationText, margin + durationLabelWidth + 5, currentY);
      currentY += 10;

      // Was client at home (inline)
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const wasHomeLabel = "Was the client at home?:";
      const wasHomeText = sessionFormData.wasClientAtHome 
        ? sessionFormData.wasClientAtHome.charAt(0).toUpperCase() + sessionFormData.wasClientAtHome.slice(1)
        : "N/A";
      doc.text(wasHomeLabel, margin, currentY);
      doc.setFont("helvetica", "normal");
      const wasHomeLabelWidth = doc.getTextWidth(wasHomeLabel);
      doc.text(wasHomeText, margin + wasHomeLabelWidth + 5, currentY);
      currentY += 10;

      // Diagnosis section (no heading, inline format)
      if (sessionFormData.primaryDiagnosis || sessionFormData.secondaryDiagnosis || sessionFormData.tertiaryDiagnosis) {
        checkNewPage(20);
        currentY += 5;

        if (sessionFormData.primaryDiagnosis) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          const primaryLabel = "Primary Dx:";
          doc.text(primaryLabel, margin, currentY);
          doc.setFont("helvetica", "normal");
          const primaryDiag = `${sessionFormData.primaryDiagnosis} ${sessionFormData.primaryDiagnosisDescription || ""}`.trim();
          const primaryLabelWidth = doc.getTextWidth(primaryLabel);
          doc.text(primaryDiag, margin + primaryLabelWidth + 5, currentY);
          currentY += 10;
        }

        if (sessionFormData.secondaryDiagnosis) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          const secondaryLabel = "Secondary Dx:";
          doc.text(secondaryLabel, margin, currentY);
          doc.setFont("helvetica", "normal");
          const secondaryDiag = `${sessionFormData.secondaryDiagnosis} ${sessionFormData.secondaryDiagnosisDescription || ""}`.trim();
          const secondaryLabelWidth = doc.getTextWidth(secondaryLabel);
          doc.text(secondaryDiag, margin + secondaryLabelWidth + 5, currentY);
          currentY += 10;
        }

        if (sessionFormData.tertiaryDiagnosis) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          const tertiaryLabel = "Tertiary Dx:";
          doc.text(tertiaryLabel, margin, currentY);
          doc.setFont("helvetica", "normal");
          const tertiaryDiag = `${sessionFormData.tertiaryDiagnosis} ${sessionFormData.tertiaryDiagnosisDescription || ""}`.trim();
          const tertiaryLabelWidth = doc.getTextWidth(tertiaryLabel);
          doc.text(tertiaryDiag, margin + tertiaryLabelWidth + 5, currentY);
          currentY += 10;
        }
      }

      yPosition = currentY + 10;

      // Client Presentation Section
      addSectionHeader("Client Presentation");
      addField("Focus of the session", sessionFormData.focusOfSession || "");
      
      if (sessionFormData.selectedSymptoms && sessionFormData.selectedSymptoms.length > 0) {
        checkNewPage(10);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Symptoms", margin, yPosition);
        yPosition += 5;
        addBulletList(sessionFormData.selectedSymptoms.map(symptom => 
          symptom === "Other" && sessionFormData.otherSymptomText 
            ? sessionFormData.otherSymptomText 
            : symptom
        ));
      }

      addField("Mood / Affect", 
        sessionFormData.moodAffect === "Other" && sessionFormData.otherMoodAffectText
          ? sessionFormData.otherMoodAffectText
          : (sessionFormData.moodAffect || "N/A"),
        true
      );

      addField("Behavior / Engagement",
        sessionFormData.behaviorEngagement === "Other" && sessionFormData.otherBehaviorEngagementText
          ? sessionFormData.otherBehaviorEngagementText
          : (sessionFormData.behaviorEngagement || "N/A"),
        true
      );

      // Interventions Section
      addSectionHeader("Interventions");
      addField("Describe the interventions used", sessionFormData.interventionsUsed || "");

      // Client Progress Section
      addSectionHeader("Client Progress");
      addField("Client response to care", sessionFormData.clientResponseToCare || "");

      if (sessionFormData.progressTowardGoals && sessionFormData.progressTowardGoals.length > 0) {
        checkNewPage(10);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Progress toward goals", margin, yPosition);
        yPosition += 5;
        addBulletList(sessionFormData.progressTowardGoals.map(goal => {
          if (goal === "Goal Met" && sessionFormData.goalMetDescription) {
            return `${goal} (${sessionFormData.goalMetDescription})`;
          }
          return goal;
        }));
      }

      if (sessionFormData.barriersToProgress && sessionFormData.barriersToProgress.length > 0) {
        checkNewPage(10);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Barriers to Progress", margin, yPosition);
        yPosition += 5;
        addBulletList(sessionFormData.barriersToProgress.map(barrier =>
          barrier === "Other" && sessionFormData.otherBarrierText
            ? sessionFormData.otherBarrierText
            : barrier
        ));
      }

      addField("Additional Notes", sessionFormData.additionalNotes || "");

      // Care Plan Section
      addSectionHeader("Care Plan");
      addField("Client Goal", sessionFormData.clientGoal || "");
      addField("Objectives", sessionFormData.objectives || "");
      addField("Interventions", sessionFormData.interventions || "");
      addField("Frequency", 
        sessionFormData.sessionFrequency === "Other" && sessionFormData.otherSessionFrequencyText
          ? sessionFormData.otherSessionFrequencyText
          : (sessionFormData.sessionFrequency || "N/A"),
        true
      );
    //   addField("Review Date", sessionFormData.reviewDate ? formatDate(sessionFormData.reviewDate) : "N/A", true);

      // Addendums Section (content only, signatures will be at bottom)
      if (addendumDataArray && addendumDataArray.length > 0) {
        addendumDataArray.forEach((addendum, index) => {
          if (addendum.addendumSigned === true && addendum.addendumText) {
            checkNewPage(20);
            yPosition += 10;
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(`Addendum # ${index + 1}`, margin, yPosition);
            yPosition += 8;
            addField("", addendum.addendumText);
          }
        });
      }

      // Show Provider Signature (main form signature)
      if (isSigned || sessionFormData.isSigned) {
        checkNewPage(80);
        yPosition += 15;
        
        // "Provider Signature" heading
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Provider Signature", margin, yPosition);
        yPosition += 8;
        
        // Separator line
        doc.setLineWidth(0.5);
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPosition, margin + 40, yPosition);
        yPosition += 10;
        
        // Signature box background (light blue)
        const boxHeight = 60;
        const boxWidth = contentWidth;
        doc.setFillColor(236, 248, 254);
        doc.roundedRect(margin, yPosition, boxWidth, boxHeight, 2, 2, 'F');
        
        // Signature text (cursive style - matching SignAndLockCard)
        // Using Brush Script MT font for authentic signature appearance
        doc.setFontSize(32);
        doc.setFont("BrushScriptMT", "normal");
        doc.setTextColor(26, 26, 26);
        doc.text(providerName || "N/A", margin + 10, yPosition + 20);
        
        // Signed by text
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(102, 102, 102);
        doc.text(`Signed by ${providerName || "N/A"}, ${providerCredentials || ""}`, margin + 10, yPosition + 28);
        
        // Date and time - use main form date/time
        if (getSignedDateFromDB() && getSignedTimeFromDB()) {
          doc.text(`${getSignedDateFromDB()}; ${getSignedTimeFromDB()}`, margin + 10, yPosition + 36);
        }
        
        yPosition += boxHeight + 15;
      }

      // Show addendum signatures at the bottom (after main provider signature)
      if (addendumDataArray && addendumDataArray.length > 0) {
        addendumDataArray.forEach((addendum, index) => {
          // Only show signed addendums
          if (addendum.addendumSigned === true && addendum.addendumSignedAt) {
            const addendumNumber = index + 1;
            
            checkNewPage(80);
            
            // Addendum signature heading
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 0, 0);
            doc.text(`Provider Signature - Addendum # ${addendumNumber}`, margin, yPosition);
            yPosition += 8;
            
            // Separator line
            doc.setLineWidth(0.5);
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, yPosition, margin + 40, yPosition);
            yPosition += 10;
            
            // Signature box background (light blue)
            const boxHeight = 60;
            const boxWidth = contentWidth;
            doc.setFillColor(236, 248, 254);
            doc.roundedRect(margin, yPosition, boxWidth, boxHeight, 2, 2, 'F');
            
            // Signature text (cursive style - matching SignAndLockCard)
            // Using Brush Script MT font for authentic signature appearance
            doc.setFontSize(32);
            doc.setFont("BrushScriptMT", "normal");
            doc.setTextColor(26, 26, 26);
            doc.text(providerName || "N/A", margin + 10, yPosition + 20);
            
            // Signed by text
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(102, 102, 102);
            doc.text(`Signed by ${providerName || "N/A"}, ${providerCredentials || ""}`, margin + 10, yPosition + 28);
            
            // Date and time from addendum
            if (addendum.addendumSignedAt) {
              const addendumSignedDate = new Date(addendum.addendumSignedAt).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric"
              });
              const addendumSignedTime = new Date(addendum.addendumSignedAt).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true
              });
              doc.text(`${addendumSignedDate}; ${addendumSignedTime}`, margin + 10, yPosition + 36);
            }
            
            yPosition += boxHeight + 15;
          }
        });
      }

      // Footer - Page numbers and header on each page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        // Add header to each page
        addHeader();
        // Add page number
        doc.setFontSize(10);
        doc.setTextColor(120);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      }

      // Generate filename
      const dateStr = formatDate(appointmentDate).replace(/\//g, "-").replace(/\s+/g, "_");
      const clientNameFormatted = clientName.replace(/\s+/g, "_");
      const hasAddendum = addendumDataArray && addendumDataArray.length > 0;
      const noteType = hasAddendum ? "ProgressNote" : "FollowUp";
      const fileName = `${noteType}_${clientNameFormatted}_${dateStr}.pdf`;
      
      doc.save(fileName);
      toast.success("Follow-up note downloaded.");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("An error occurred while generating PDF.");
    }
  };

  return generatePDF;
};

export default FollowUpPDFGenerator;

